import styles from "./InviteFriendsDialog.module.scss";
import { useTranslation } from "../../i18n";
import { dateLocale } from "../../i18n/dateLocale";
import classNames from "classnames";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import { format } from "date-fns";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import { InviteIcon } from "../Icons/Invite";
import { useAlert } from "../../context/AlertContext";
import { useState } from "react";
import { fetcher } from "../../utils/fetcher";


interface EventForInvite {
  id: string;
  event_title: string;
  event_start_datetime: Date | string;
  location?: string;
  address?: string;
}

interface InviteFriendsDialogProps {
  event: EventForInvite;
  dialogOpen: boolean;
  setDialogOpen: (open: boolean) => void;
  isCollection?: boolean;
}

export const InviteFriendsDialog = ({
  event,
  dialogOpen,
  setDialogOpen,
  isCollection = false,
}: InviteFriendsDialogProps) => {
  const { t } = useTranslation();
  const [emails, setEmails] = useState<string[]>([]);
  const [currentEmail, setCurrentEmail] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setAlert } = useAlert();
  const { event_title, event_start_datetime, location, address } = event;
  const displayLocation = location || address || '';

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleAddEmail = () => {
    const trimmedEmail = currentEmail.trim();
    
    if (!trimmedEmail) {
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError("invite.errors.invalidEmail");
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError("invite.errors.duplicate");
      return;
    }

    setEmails([...emails, trimmedEmail]);
    setCurrentEmail("");
    setError("");
  };

  const handleRemoveEmail = (emailToRemove: string) => {
    setEmails(emails.filter(email => email !== emailToRemove));
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail();
    }
  };

  const handleInviteFriends = async () => {
    if (emails.length === 0) {
      setError("invite.errors.empty");
      return;
    }

    setLoading(true);
    const payload = {
      event_link: `${window.location.origin}/event/${event.id}`,
      list_of_email_addresses: emails,
    };
    
    try {
      await fetcher({
        url: `/event/invite/${event.id}`,
        method: "POST",
        body: JSON.stringify(payload),
      });
      setEmails([]);
      setCurrentEmail("");
      setAlert(
        true,
        "success",
        t("invite.alerts.sent"),
      );
      setDialogOpen(false);
    } catch (error) {
      setAlert(
        true,
        "error",
        t("invite.alerts.failed"),
      );
      console.error("Error inviting friends:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={dialogOpen}
        disableEscapeKeyDown
        onClose={() => {}}
        slotProps={{
          backdrop: {
            onClick: () => {},
          },
        }}
        aria-labelledby="invite-dialog-title"
        maxWidth="sm"
        className={styles.dialog}
      >
        <DialogTitle id="invite-dialog-title" className={styles.dialogTitle}>
          <InviteIcon />
          {t("invite.title")}
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          <p className={styles.title}>{event_title}</p>
          {!isCollection && event_start_datetime && (
            <>
              <p className={classNames(styles.text, styles.date)}>
                <CalendarMonthOutlinedIcon />
                {format(new Date(event_start_datetime), t("formats.dateLong"), {
                  locale: dateLocale(),
                })}
                {t("eventCard.from")}{" "}
                {format(new Date(event_start_datetime), t("formats.time"), {
                  locale: dateLocale(),
                })}
              </p>
              {displayLocation && (
                <p className={styles.text}>
                  <PinDropOutlinedIcon />
                  {displayLocation}
                </p>
              )}
            </>
          )}
          <div id="invite-dialog-emails" className={styles.emails}>
            <TextField
              type="email"
              label={t("invite.email")}
              placeholder={t("invite.emailPlaceholder")}
              variant="outlined"
              size="small"
              fullWidth
              value={currentEmail}
              onChange={(e) => {
                setCurrentEmail(e.target.value);
                setError("");
              }}
              onKeyPress={handleKeyPress}
              onBlur={handleAddEmail}
              error={!!error}
              helperText={t(error)}
              disabled={loading}
              InputProps={{
                startAdornment: <EmailOutlinedIcon className={styles.emailIcon} />,
              }}
            />
            
            {emails.length > 0 && (
              <div className={styles.chipContainer}>
                {emails.map((email, idx) => (
                  <Chip
                    key={idx}
                    label={email}
                    onDelete={() => handleRemoveEmail(email)}
                    color="primary"
                    variant="outlined"
                    className={styles.emailChip}
                    disabled={loading}
                  />
                ))}
              </div>
            )}
          </div>
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => {
              setEmails([]);
              setCurrentEmail("");
              setError("");
              setDialogOpen(false);
            }}
            disabled={loading}
          >
            {t("common.cancel")}
          </Button>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={handleInviteFriends}
            disabled={loading || emails.length === 0}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : null}
          >
            {loading ? t("invite.sending") : t("invite.invite")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

